import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useHistory, useLocation } from 'react-router-dom';

import CustomLoading from '../../../util_components/custom_loading';
import SummaryCards from '../shared/summary_cards';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import Pager from '../shared/pager';
import TeacherFilter from '../shared/teacher_filter';
import {
  CheckChip,
  FilterBar,
  LabeledSegmented,
  SelectFilter,
  Segmented
} from '../shared/controls';
import { Ledger, StatusCell } from '../shared/cells';
import StatusPill from '../shared/status_pill';
import { toggle_sort } from '../shared/use_sortable';
import ShadowBanner from './shadow_banner';
import EarningsBulkBar from './earnings_bulk_bar';
import BulkSkip from '../shared/bulk_skip';
import { breakdown_eligibility, bulk_breakdown_state } from '../email_send/logic';
import { fetch_csv, fetch_l1 } from '../api';
import {
  EM_DASH,
  csv_filename,
  extract_csv_payload,
  fmt,
  is_permission_error,
  load_failed_text,
  needs_review,
  needs_review_reason,
  not_priced_text
} from '../format';
import { AMOUNTS_NEED_REVIEW_TEXT, NEEDS_REVIEW_TEXT, l1_view } from './adapters';
import {
  adjustment_info_view,
  adjustment_items,
  earned_items,
  payable_bridge_items
} from './l1_ledger';
import InfoLedger from './info_ledger';
import { replace_search, teacher_picker_text } from '../shared/url_query';
import './overview.css';
import { shows_versions_link, versions_link } from '../close_versions/params';
import { l1_invoice_cell } from '../invoices/adapters';
import {
  TEACHER_STATUS_OPTIONS,
  apply_l1_drafts,
  apply_l1_sort,
  clear_l1_filters,
  csv_params,
  l1_filter_summary,
  l1_filters_on,
  l1_params,
  l1_state_for_month,
  l1_url_search,
  set_l1_filter
} from './params';
import {
  earnings_pill,
  page_pick_state,
  picked_uuids,
  toggle_page,
  toggle_pick
} from './earnings_logic';

const BULK_BAR_ID = 'tp-overview-bulk';

// A-52: the bulk bar's breakdown send; disabled with the reason when no pick is verified.
const BulkBreakdownButton = ({ uuids, rows, on_send }) => {
  const st = bulk_breakdown_state(uuids, rows);
  return (
    <>
      <button
        type="button"
        className="tp-btn tp-es-bulk-send"
        aria-label="Send payment breakdown to selected"
        aria-disabled={st.enabled ? undefined : 'true'}
        onClick={st.enabled ? on_send : undefined}
      >
        <span className="tp-lbl-long">Send payment breakdown</span>
        <span className="tp-lbl-short">Send breakdown</span>
      </button>
      {st.enabled ? null : <span className="tp-es-why tp-es-bulk-why">{st.reason}</span>}
    </>
  );
};

/* Overview Level 1: one row per teacher for the selected pay month. */
const PAGE_SIZE = 100;

// The Earnings page choices as segments: the pill words, not the long dropdown labels.
const EARNINGS_SEGMENTS = [
  { value: '1', label: 'Visible' },
  { value: '0', label: 'Hidden' }
];

const OverviewLevel1 = ({
  pay_month,
  month_label,
  view_state,
  on_view_state,
  on_error,
  on_notify = () => {},
  on_open_teacher,
  on_send_breakdown
}) => {
  const history = useHistory();
  const location = useLocation();
  const own_path = useRef(location.pathname);
  const [loading, setLoading] = useState(true);
  const [load_error, setLoadError] = useState('');
  const [rows, setRows] = useState([]);
  const [stat_cards, setStatCards] = useState([]);
  const [total_count, setTotalCount] = useState(0);
  const [shadow_banner, setShadowBanner] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [picked, setPicked] = useState({});
  const page_box = useRef(null);
  // drafts mirror the bar's controls; each change applies at once. State is owned by OverviewTab.
  const { applied, offset, drafts, sort } = view_state;

  // only the latest request may render — a slow month-A response must never land under month B.
  const request_seq = useRef(0);
  // last args, so the LoadFailed retry button can replay the exact request that failed.
  const last_args = useRef([0, {}, null]);

  // keep: a filter, sort or page change keeps the current rows and tiles on screen (faded) while it reloads.
  const load = useCallback(
    (next_offset, filters, next_sort, keep = false) => {
      last_args.current = [next_offset, filters, next_sort, keep];
      request_seq.current += 1;
      const seq = request_seq.current;
      if (!pay_month) {
        // never leave a previous month's rows, cards or banner on screen without a month.
        setRows([]);
        setStatCards([]);
        setTotalCount(0);
        setShadowBanner(false);
        setLoading(false);
        setLoadError('');
        return;
      }
      setLoading(true);
      setLoadError('');
      if (!keep) {
        setRows([]);
        setStatCards([]);
        setShadowBanner(false);
      }
      fetch_l1(l1_params({ pay_month, ...filters, sort: next_sort }, PAGE_SIZE, next_offset))
        .then((data) => {
          if (seq !== request_seq.current) return;
          const view = l1_view(data);
          setRows(view.rows);
          setStatCards(view.stat_cards);
          setTotalCount(view.total_count);
          setShadowBanner(view.shadow_banner);
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== request_seq.current) return;
          // Permission errors also toast (module-wide gate); other failures are inline-only, matching Penalties.
          if (is_permission_error(e)) on_error(e, 'Failed to load the teacher payment overview.');
          setRows([]);
          setStatCards([]);
          setTotalCount(0);
          setLoading(false);
          // an inline, retryable failure — never a silent "0 teachers this month" state. shows the
          // server's own LOOKUP_FAILED sentence when the body carries one.
          setLoadError(load_failed_text(e, 'Failed to load the teacher payment overview.'));
        });
    },
    [pay_month, on_error]
  );

  const retry = () => load(...last_args.current);

  // (Re)mount after Back keeps the page and sort; a month change goes to page 1.
  useEffect(() => {
    setPicked({});
    const next = l1_state_for_month(view_state, pay_month);
    if (next !== view_state) on_view_state(next);
    load(next.offset, next.applied, next.sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay_month]);

  // A new filter clears picks, so a bulk action never reaches teachers no longer shown.
  const run_filters = (next) => {
    setPicked({});
    on_view_state(next);
    load(0, next.applied, next.sort, true);
  };
  const set_filter = (key, value) => run_filters(set_l1_filter(view_state, key, value));
  const clear_filters = () => run_filters(clear_l1_filters(view_state));

  const on_teacher_change = (next_teacher) =>
    run_filters(
      apply_l1_drafts({
        ...view_state,
        drafts: {
          ...view_state.drafts,
          search: next_teacher.teacher_uuid,
          search_display: next_teacher.display
        }
      })
    );

  // The URL mirrors the filters and sort (replace: not history steps); month and other params are kept.
  useEffect(() => {
    // Never rewrite another page's URL (a push away lands before this tab unmounts).
    replace_search(history, own_path.current, (search) => l1_url_search(search, applied, sort));
  }, [applied, sort, history, location.pathname, location.search]);

  const go_page = (next_offset) => {
    on_view_state((s) => ({ ...s, offset: next_offset }));
    load(next_offset, applied, sort, true);
  };

  // A header click toggles; the card sort select sets key and direction directly (same reload).
  const set_sort = (next_sort) => {
    setPicked({});
    const next = apply_l1_sort(view_state, next_sort);
    on_view_state(next);
    load(0, next.applied, next.sort, true);
  };
  const on_sort = (key) => set_sort(toggle_sort(sort, key));

  // the tie-out CSV is a first-class button — it is the input the parity harness compares against
  // the manual sheet. Server-generated so the export never diverges from the stored numbers (and
  // never re-derives anything client-side).
  const download_csv = () => {
    if (downloading || !pay_month) return;
    setDownloading(true);
    fetch_csv(csv_params({ pay_month, ...applied, sort }))
      .then((body) => {
        const csv = extract_csv_payload(body);
        if (!csv) {
          on_error(new Error('empty csv'), 'The CSV export came back empty.');
          setDownloading(false);
          return;
        }
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), csv_filename(pay_month));
        setDownloading(false);
      })
      .catch((e) => {
        on_error(e, 'Failed to download the tie-out CSV.');
        setDownloading(false);
      });
  };

  // Payable is the decision number; a not-priced or needs-review month says so instead.
  const payable_cell = (t) => {
    if (needs_review(t)) {
      return (
        <StatusPill tone="orange">
          <span title={needs_review_reason(t)}>{NEEDS_REVIEW_TEXT}</span>
        </StatusPill>
      );
    }
    const np = not_priced_text(t);
    if (np) {
      return (
        <span className="tp-muted" title={np}>
          {EM_DASH}
        </span>
      );
    }
    return <span className="tp-c-amt">{fmt(t.payable_amount, 2)}</span>;
  };

  // Earned: the earnings ledger, or the one line that stands in for it.
  const earned_cell = (t) => {
    if (needs_review(t)) return <div className="tp-c-line">{needs_review_reason(t)}</div>;
    const np = not_priced_text(t);
    if (np) return <div className="tp-c-line">{np}</div>;
    return <Ledger items={earned_items(t)} />;
  };
  const adjustments_cell = (t) => {
    if (needs_review(t)) {
      return (
        <div className="tp-ov-review" title={needs_review_reason(t)}>
          {AMOUNTS_NEED_REVIEW_TEXT}
        </div>
      );
    }
    if (not_priced_text(t)) return <span className="tp-muted">{EM_DASH}</span>;
    return (
      <>
        <Ledger items={[...adjustment_items(t), ...payable_bridge_items(t)]} />
        <InfoLedger view={adjustment_info_view(t)} />
      </>
    );
  };

  // closed now or closed before (reopen_count); a never-closed month has no versions.
  const versions_button = (t, className) =>
    shows_versions_link(t) ? (
      <button
        type="button"
        className={className}
        aria-label={`Versions of ${t.teacher_name}'s month`}
        onClick={() => history.push(versions_link(pay_month, t.teacher_uuid))}
      >
        Versions
      </button>
    ) : null;
  // A-52: only a verified (closed) teacher-month sends; otherwise the reason shows under the button.
  const send_button = (t, className) => {
    if (!on_send_breakdown) return null;
    const el = breakdown_eligibility(t);
    const why_id = `tp-ov-send-why-${t.teacher_uuid}`;
    return (
      <span className="tp-ov-send">
        <button
          type="button"
          className={className}
          aria-label={`Send payment breakdown to ${t.teacher_name}`}
          aria-disabled={el.enabled ? undefined : 'true'}
          aria-describedby={el.enabled ? undefined : why_id}
          onClick={
            el.enabled
              ? () =>
                  on_send_breakdown({
                    teacher_uuids: [t.teacher_uuid],
                    label: t.teacher_name || t.teacher_uuid
                  })
              : undefined
          }
        >
          Send breakdown
        </button>
        {el.enabled ? null : (
          <span id={why_id} className="tp-es-why">
            {el.reason}
          </span>
        )}
      </span>
    );
  };
  const row_actions = (t, className, wrap) => (
    <span className={wrap}>
      <button type="button" className={className} onClick={() => on_open_teacher(t)}>
        View
      </button>
      {versions_button(t, className)}
      {send_button(t, className)}
    </span>
  );

  const page_pick = page_pick_state(picked, rows);
  useLayoutEffect(() => {
    if (page_box.current) page_box.current.indeterminate = page_pick.some;
  }, [page_pick.some]);
  const selected_uuids = picked_uuids(picked);
  const has_bulk_bar = rows.some((r) => 'earnings_visible' in r);
  const on_earnings_done = () => {
    setPicked({});
    load(offset, applied, sort);
  };

  const who = (t, name) => {
    const cell = l1_invoice_cell(t);
    const pill = earnings_pill(t);
    return (
      <div className="tp-c-who">
        <div className="tp-c-name">{name}</div>
        {t.teacher_slug ? <div className="tp-c-meta">{t.teacher_slug}</div> : null}
        <div className="tp-c-meta tp-ov-pills">
          <span>
            Invoice <StatusPill tone={cell.tone}>{cell.label}</StatusPill>
          </span>
          <span title="Whether this teacher can see the Earnings page in the teacher app">
            Earnings page <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
          </span>
        </div>
      </div>
    );
  };

  // Four tracks: teacher · earned · adjustments · payable; a card below 660px. Teacher takes the spare width.
  const columns = [
    {
      key: 'select',
      role: 'select',
      label: <span className="sr-only">Select</span>,
      track: '34px',
      render: (t) => (
        <input
          type="checkbox"
          aria-label={`Select ${t.teacher_name || t.teacher_uuid}`}
          checked={!!picked[t.teacher_uuid]}
          onChange={() => setPicked((p) => toggle_pick(p, t.teacher_uuid))}
        />
      )
    },
    {
      key: 'teacher',
      role: 'identifier',
      label: 'Teacher',
      sort_key: 'teacher_name',
      render: (t) =>
        who(
          t,
          <button type="button" className="tp-link tp-ov-name" onClick={() => on_open_teacher(t)}>
            {t.teacher_name || '--'}
          </button>
        )
    },
    {
      key: 'earned',
      label: 'Earned',
      track: { compact: '170px', full: '220px' },
      sorts: [
        { sort_key: 'one_on_one', label: '1-on-1' },
        { sort_key: 'group_class', label: 'Group classes' },
        { sort_key: 'trial', label: 'Trial' },
        { sort_key: 'incentives', label: 'Incentives' }
      ],
      render: earned_cell
    },
    {
      key: 'adjustments',
      label: 'Adjustments',
      align: 'right',
      track: { compact: '150px', full: '200px' },
      sorts: [
        { sort_key: 'additional', label: 'Additional payments' },
        { sort_key: 'penalties', label: 'Penalties' },
        { sort_key: 'waivers', label: 'Waivers' }
      ],
      render: adjustments_cell
    },
    {
      // Status track: the payable carries the row's state (a needs-review row says so here).
      key: 'payable_amount',
      role: 'status',
      label: 'Payable',
      align: 'right',
      track: { compact: '156px', full: '176px' },
      sort_key: 'net',
      render: (t) => (
        <StatusCell
          chip={payable_cell(t)}
          actions={row_actions(t, 'tp-btn-out tp-ov-act', 'tp-c-acts tp-actions')}
        />
      ),
      card_render: payable_cell
    },
    {
      key: 'actions',
      role: 'actions',
      label: 'Actions',
      in_table: false,
      card_render: (t) => row_actions(t, 'tp-btn-out', 'tp-row-actions tp-actions')
    }
  ];

  const page_start = total_count === 0 ? 0 : offset + 1;
  const page_end = Math.min(offset + rows.length, total_count);
  // A teacher filter opened from the URL (no picked name yet) shows the loaded row's name once it lands.
  const teacher_row = applied.search ? rows.find((r) => r.teacher_uuid === applied.search) : null;
  const teacher_display = drafts.search_display || (teacher_row ? teacher_row.teacher_name : '');
  const summary = load_error
    ? null
    : l1_filter_summary({
        total: total_count,
        month_label,
        applied,
        teacher_name: teacher_display,
        loading
      });

  return (
    <div className="tp-ov">
      <ShadowBanner show={shadow_banner} />

      <div className={loading && rows.length ? 'tp-reloading' : undefined}>
        <SummaryCards cards={stat_cards} />
      </div>

      <FilterBar
        label="Overview filters"
        summary={
          summary ? (
            <>
              Showing <b>{summary.lead}</b>
              {summary.rest}
            </>
          ) : null
        }
      >
        <div className="tp-fsearch">
          <TeacherFilter
            id="tp-l1-teacher-filter"
            label="Teacher"
            value={{
              teacher_uuid: applied.search,
              display: teacher_picker_text(applied.search, teacher_display)
            }}
            onChange={on_teacher_change}
          />
        </div>
        <Segmented
          label="Teachers"
          options={[
            { value: '', label: 'All teachers' },
            { value: 'NEEDS_REVIEW', label: 'Needs review' }
          ]}
          value={drafts.review || ''}
          onChange={(v) => set_filter('review', v)}
        />
        <SelectFilter
          id="tp-l1-status"
          label="Teacher status"
          value={drafts.status}
          all_label="All statuses"
          options={TEACHER_STATUS_OPTIONS}
          onChange={(v) => set_filter('status', v)}
        />
        <LabeledSegmented
          label="Earnings page"
          options={[{ value: '', label: 'Any' }, ...EARNINGS_SEGMENTS]}
          value={drafts.earnings || ''}
          onChange={(v) => set_filter('earnings', v)}
        />
        {l1_filters_on(applied) ? (
          <button type="button" className="tp-link tp-fbar-end" onClick={clear_filters}>
            Clear filters
          </button>
        ) : null}
      </FilterBar>

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title">All teachers · {month_label}</span>
            <span className="tp-card-hd-sub">
              {load_error
                ? ''
                : total_count === 0
                  ? 'No teachers'
                  : `Showing ${page_start}–${page_end} of ${total_count}`}
            </span>
          </span>
          <span className="tp-card-hd-acts">
            {!loading && !load_error && rows.length ? (
              <CheckChip
                className="tp-ov-pick-all"
                inputRef={page_box}
                checked={page_pick.all}
                onChange={() => setPicked((p) => toggle_page(p, rows))}
              >
                Select all on page ({rows.length})
              </CheckChip>
            ) : null}
            {/* aria-disabled, not disabled: the pressed button keeps focus while it prepares. */}
            <button
              type="button"
              className="tp-btn-out"
              onClick={download_csv}
              aria-disabled={downloading || !pay_month ? 'true' : undefined}
            >
              {downloading ? 'Preparing…' : '↓ Tie-out CSV'}
            </button>
          </span>
        </div>
        {!loading && !load_error && rows.length ? (
          <BulkSkip count={has_bulk_bar ? selected_uuids.length : 0} target_id={BULK_BAR_ID} />
        ) : null}
        {loading && !rows.length ? (
          <CustomLoading />
        ) : load_error ? (
          <LoadFailed text={load_error} on_retry={retry} />
        ) : (
          <div className={loading ? 'tp-reloading' : undefined} aria-busy={loading || undefined}>
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(t) => t.teacher_uuid}
              rowClassName={(t) => (picked[t.teacher_uuid] ? 'tp-row-sel' : '')}
              sort={sort}
              on_sort={on_sort}
              on_sort_set={set_sort}
              emptyText="No teacher payment rows for this month and filter."
              card_below={660}
              responsive
            />
          </div>
        )}
      </div>

      <Pager
        offset={offset}
        rows={rows.length}
        total={total_count}
        page_size={PAGE_SIZE}
        on_page={go_page}
      />

      {/* After the list and pager: one padded bar, sticky at the viewport bottom while teachers are
          selected (it never covers the pager or the last row). */}
      {has_bulk_bar ? (
        <EarningsBulkBar
          id={BULK_BAR_ID}
          uuids={selected_uuids}
          on_clear={() => setPicked({})}
          on_done={on_earnings_done}
          on_notify={on_notify}
        >
          {on_send_breakdown ? (
            <BulkBreakdownButton
              uuids={selected_uuids}
              rows={rows}
              on_send={() =>
                on_send_breakdown({
                  teacher_uuids: selected_uuids,
                  label: `${selected_uuids.length} selected`
                })
              }
            />
          ) : null}
        </EarningsBulkBar>
      ) : null}
    </div>
  );
};

export default OverviewLevel1;
